<template>
    <div class="custom-block-attribute-picker">
        <div class="filter-menu">
            <SupsisButton v-if="selectedMenu.length" ghost small @click="prev">
                <span class="material-icons"> chevron_left </span>
            </SupsisButton>
            <div class="breadcrumb-item" v-for="menu in selectedMenu" :key="menu">
                <span> {{ findAttributeByValue(menu).label }} ✦ </span>
            </div>
        </div>

        <div class="custom-blocks" v-if="!selectedMenu.length">
            <div class="block-item" v-for="block in customBlocks" :key="block.id" @click="selectBlock(block)">
                <span class="material-icons">extension</span>
                <span class="name">{{ block.name || "Custom Block" }}</span>
            </div>
        </div>

        <div class="attribute-list" v-else>
            <div class="attr-select">
                <div
                    class="picker-item"
                    v-for="attr in filterAttributes()"
                    @click="() => select(attr.value)"
                    :key="attr.value"
                >
                    <span class="name">{{ attr.label }}</span>
                    <span class="description" v-if="attr.description">{{ attr.description }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";
import flatDiagram from "../../../utils/flatDiagram";

export default {
    name: "CustomBlockAttributePicker",
    components: {
        SupsisButton,
    },
    props: {
        onPicked: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            searchText: "",
            attributes: [],
            selectedMenu: [],
            customBlocks: [],
            selectedBlock: null,
        };
    },
    computed: {
        ...mapGetters({
            board: "CB/board",
        }),
    },
    methods: {
        listCustomBlocks() {
            const storyId = this.$route.params.id;
            const board = this.board(storyId);
            const flatted = flatDiagram(board.diagram);
            return flatted.filter((block) => block.type === "CUSTOM_BLOCK");
        },

        generateAttributesForBlock(block) {
            const blockId = block.id;
            const basePrefix = `block.${blockId}`;

            // Get outputSchema from runtime or definition
            let outputSchema = block.payload?.outputSchema;

            console.log("CustomBlockAttributePicker - Block:", block);
            console.log("CustomBlockAttributePicker - Runtime outputSchema:", outputSchema);

            // If no runtime outputSchema, try to load from imported block definition
            if (!outputSchema || (typeof outputSchema === "object" && Object.keys(outputSchema).length === 0)) {
                const customBlockId = block.payload?.customBlockId;
                if (customBlockId) {
                    // Try to get outputSchema from imported block's definition
                    const importedBlocks = this.$store.state.customBlocks?.importedBlocks || [];
                    const importedBlock = importedBlocks.find((b) => b.blockId === customBlockId);
                    if (importedBlock && importedBlock.block?.outputSchema) {
                        outputSchema = importedBlock.block.outputSchema;
                        console.log("CustomBlockAttributePicker - Using definition outputSchema:", outputSchema);
                    }
                }
            }

            // If still no outputSchema, show placeholder attributes
            if (!outputSchema || (typeof outputSchema === "object" && Object.keys(outputSchema).length === 0)) {
                console.log("No outputSchema found, showing placeholder attributes");
                return [
                    {
                        label: "*",
                        value: `${basePrefix}`,
                        description: "All values (block not executed yet)",
                    },
                    {
                        label: "success",
                        value: `${basePrefix}.success`,
                        description: "Execution success status (boolean)",
                    },
                    {
                        label: "result",
                        value: `${basePrefix}.result`,
                        description: "Execution result (object)",
                    },
                    {
                        label: "error",
                        value: `${basePrefix}.error`,
                        description: "Error message (if any)",
                    },
                ];
            }

            let dataToProcess = outputSchema;

            // Generate attributes from outputSchema (or unwrapped result)
            const attrs = this.generateDataAttributes(basePrefix, dataToProcess);
            console.log("Generated attributes:", attrs);
            return attrs;
        },

        generateDataAttributes(prefix, data) {
            const attributes = [];

            if (!data) return [];

            if (Array.isArray(data) && data.length > 0) {
                attributes.push({
                    label: "*",
                    value: `${prefix}.$..*`,
                    description: "All array elements",
                });

                const firstItem = data[0];
                if (firstItem && typeof firstItem === "object") {
                    Object.entries(firstItem).forEach(([key, value]) => {
                        const _prefix = `${prefix}.${key}`;
                        const subAttributes = this.generateDataAttributes(_prefix, value);
                        attributes.push({
                            label: key,
                            value: _prefix,
                            description: `${key} (${this.getValueType(value)})`,
                            subAttributes,
                        });
                    });
                }
            } else if (typeof data === "object" && data !== null) {
                attributes.push({
                    label: "*",
                    value: `${prefix}.$..*`,
                    description: "All object fields",
                });

                Object.entries(data).forEach(([key, value]) => {
                    const _prefix = `${prefix}.${key}`;
                    const subAttributes = this.generateDataAttributes(_prefix, value);

                    attributes.push({
                        label: key,
                        value: _prefix,
                        description: `${key} (${this.getValueType(value)})`,
                        subAttributes: subAttributes && subAttributes.length > 0 ? subAttributes : undefined,
                    });
                });
            } else {
                return undefined;
            }

            return attributes.length > 0 ? attributes : undefined;
        },

        getValueType(value) {
            if (value === null) return "null";
            if (Array.isArray(value)) return "array";
            if (typeof value === "object") return "object";
            if (typeof value === "string") return "string";
            if (typeof value === "number") return "number";
            if (typeof value === "boolean") return "boolean";
            return "unknown";
        },

        selectBlock(block) {
            this.selectedBlock = block;
            this.attributes = this.generateAttributesForBlock(block);
            this.selectedMenu.push("root");
        },

        filterAttributes() {
            const parentAttribute = this.getParentAttribute();
            let attrs = parentAttribute ? parentAttribute.subAttributes : this.attributes;

            return attrs.filter((attr) => {
                if (this.searchText) {
                    return (attr.label || "").toLowerCase().includes(this.searchText.toLowerCase());
                }
                return true;
            });
        },

        getParentAttribute() {
            if (this.selectedMenu.length <= 1) return null;
            const attributes = this.getFlatAttributes();
            const parent = this.selectedMenu[this.selectedMenu.length - 1];
            return attributes.find((x) => x.value === parent);
        },

        getFlatAttributes() {
            const flat = (attr) => {
                if (attr.subAttributes) {
                    return [attr, ...attr.subAttributes.flatMap(flat)];
                }
                return [attr];
            };
            return this.attributes.flatMap(flat);
        },

        select(value) {
            const attributes = this.getFlatAttributes();
            const attr = attributes.find((x) => x.value === value);
            if (Array.isArray(attr.subAttributes) && attr.subAttributes.length) {
                this.selectedMenu.push(value);
            } else {
                this.onPicked(value);
            }
        },

        prev() {
            if (this.selectedMenu.length === 1) {
                this.selectedMenu = [];
                this.selectedBlock = null;
            } else {
                this.selectedMenu.pop();
            }
        },

        findAttributeByValue(value) {
            if (value === "root") {
                return { label: this.selectedBlock?.name || "Custom Block" };
            }
            const attributes = this.getFlatAttributes();
            return attributes.find((x) => x.value === value);
        },
    },
    created() {
        this.customBlocks = this.listCustomBlocks();
    },
};
</script>

<style lang="scss" scoped>
.custom-block-attribute-picker {
    background: #fff;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    max-height: 600px;
    min-height: 200px;
    user-select: none;
    width: 100%;
    padding: 5px;

    .filter-menu {
        display: flex;
        align-items: center;
        gap: 3px;
        margin-bottom: 10px;

        .supsis-button {
            border: none !important;
            font-size: 13px;
            height: 25px;
        }

        .breadcrumb-item {
            display: flex;
        }
    }

    .custom-blocks {
        .block-item {
            display: flex;
            align-items: center;
            padding: 8px;
            cursor: pointer;
            border-radius: 4px;
            margin-bottom: 4px;

            &:hover {
                background-color: #f0f4f7;
            }

            .material-icons {
                margin-right: 8px;
                color: #9c27b0;
            }

            .name {
                color: #22272d;
                font-size: 14px;
            }
        }
    }

    .attribute-list {
        flex-grow: 1;
        overflow-x: hidden;
        overflow-y: auto;
        padding: 5px;
        margin: 0px 5px 0px 5px;

        .picker-item {
            align-items: center;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            padding: 8px;
            transition: background-color 0.2s;
            border-radius: 4px;

            &:hover {
                background-color: #f0f4f7;
            }

            .name {
                color: #22272d;
                font-size: 14px;
            }

            .description {
                color: #9ba6b3;
                font-size: 12px;
            }
        }
    }
}
</style>
